import { useEffect, useState } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { CgPen, CgTrash } from "react-icons/cg"; // icons for edit/delete
import { Modal } from "react-bootstrap";
import CustomModal from "../Commaon/CustomModal";

const CitiesList = () => {
  const [cities, setCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [addModel, setAddModel] = useState(false);
  const [editModel, setEditModel] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [name, setName] = useState("");
  const [editName, setEditName] = useState("");
  const [editId, setEditId] = useState(null);
  const [deleteModel, setDeleteModel] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [viewModel, setViewModel] = useState(false);
  const [viewCity] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // search + pagination states
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const getCities = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/city/getall`, {
        method: "GET",
      });
      const data = response.data;
      if (data.s === 200) {
        setCities(data.r || []);
        setFilteredCities(data.r || []);
      }
    } catch (err) {
      console.error("Failed to fetch cities:", err);
      setSnack({
          notification: { message: "Error fetching service locations", type: "error" },
      });
      setCities([]);
      setFilteredCities([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    getCities();
  }, []);

  // search filter
  useEffect(() => {
    if (!query.trim()) {
      setFilteredCities(cities);
    } else {
      setFilteredCities(
        cities.filter((c) =>
          c.name?.toLowerCase().includes(query.toLowerCase())
        )
      );
    }
    setPageNo(1); // reset to first page on search
  }, [query, cities]);

  // pagination slice
  const startIndex = (pageNo - 1) * limit;
  const paginatedCities = filteredCities.slice(startIndex, startIndex + limit);

  // action handlers
  const editCity = (id) => {
    const city = cities.find((c) => c._id === id);
    if (city) {
      setEditId(id);
      setName(city.name);
      setEditName(city.name);
      setEditModel(true);
    }
  };

  const deleteCity = (id) => {
    setDeleteModel(true);
    setDeleteId(id);
  };

  const toggleCityStatus = async (city) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/city/${city._id}/toggle`, {
        method: "PATCH",
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        setSnack({
          notification: { message: "Service location status updated", type: "success" },
        });
        getCities();
      }
    } catch (err) {
      setSnack({
        notification: { message: "Error updating service location status", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  const addCity = () => {
    setAddModel(true);
  };

  const visibleIds = paginatedCities.map((city) => city._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const runBulkAction = async (action) => {
    if (!selectedIds.length) {
      setSnack({
        notification: { message: "Select at least one service location", type: "error" },
      });
      return;
    }

    try {
      setLoading(true);
      await Promise.all(
        selectedIds.map((id) => {
          if (action === "delete") {
            return AuthInterceptor(`/admin/city/${id}`, { method: "DELETE" });
          }
          const city = cities.find((item) => item._id === id);
          if (city?.isActive === (action === "activate")) return Promise.resolve();
          return AuthInterceptor(`/admin/city/${id}/toggle`, { method: "PATCH" });
        })
      );
      setSnack({
        notification: { message: "Bulk action completed", type: "success" },
      });
      setSelectedIds([]);
      getCities();
    } catch (error) {
      setSnack({
        notification: { message: "Bulk action failed", type: "error" },
      });
    } finally {
      setLoading(false);
    }
  };

  // API calls for add, edit, delete
  const handleAddCity = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setSnack({
        notification: { message: "Service location name is required", type: "error" },
      });
      return;
    }

    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/city/add`, {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      const data = response.data;

      if (data.s === 201) {
        setCities((prev) => [...prev, data.r]);
        setFilteredCities((prev) => [...prev, data.r]);
        setSnack({
          notification: { message: "Service location added successfully", type: "success" },
        });
        setAddModel(false);
        setName("");
      } else {
        throw new Error(data.m || "Failed to add city");
      }
    } catch (err) {
      console.error("Error adding city:", err);
      setSnack({
        notification: {
          message: err.message || "Error adding city",
          type: "error",
        },
      });
    } finally {
      getCities();
      setName("");
      setLoading(false);
    }
  };

  const handleEditCity = async (e) => {
    e.preventDefault();
    if (!editName.trim()) {
      setSnack({
        notification: { message: "Service location name is required", type: "error" },
      });
      return;
    }
    if (editName === name) {
      setSnack({
        notification: { message: "Service location name is not changed", type: "alert" },
      });
      return;
    }
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/city/${editId}`, {
        method: "PUT",
        body: JSON.stringify({ name: editName }),
      });
      const data = response.data;

      if (data.s === 200) {
        setSnack({
          notification: {
            message: "Service location updated successfully",
            type: "success",
          },
        });
      }
    } catch (err) {
      console.error("Error updating city:", err);
      setSnack({
        notification: {
          message: err.message || "Error updating city",
          type: "error",
        },
      });
    } finally {
      getCities();
      setLoading(false);
      setEditModel(false);
      setEditName("");
      setEditId(null);
      setName("");
    }
  };

  const handleDeleteCity = async (id) => {
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/city/${id}`, {
        method: "DELETE",
      });
      const data = response.data;

      if (data.s === 200) {
        const updatedCities = data.r || [];
        setCities(updatedCities);
        setFilteredCities(updatedCities);
        setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
        setSnack({
          notification: {
            message: "Service location deleted successfully",
            type: "success",
          },
        });
      } else {
        throw new Error(data.m || "Failed to delete service location");
      }
    } catch (err) {
      console.error("Error deleting city:", err);
      setSnack({
        notification: {
          message: err.message || "Error deleting city",
          type: "error",
        },
      });
    } finally {
      setLoading(false);
      setDeleteModel(false);
      setDeleteId(null);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && (
        <Toaster
          notification={snack.notification}
          onClose={() => setSnack(null)}
        />
      )}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        {/* Header + Page Size */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <h2 className="mb-2 mb-md-0">Service Locations</h2>
          <div>
            <select
              className="form-select form-select-sm "
              value={limit}
              onChange={(e) => {
                setPageNo(1);
                setLimit(Number(e.target.value));
              }}
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-2">
          <input
            className="form-control"
            style={{ width: "40vw" }}
            placeholder="Search service locations..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn btn-outline-custom">Search</button>
            </div>
          <button
            className="btn btn-outline-custom"
            onClick={() => addCity()}
          >
            Add Service Location
          </button>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <span className="text-muted small">{selectedIds.length} selected</span>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("activate")}>
            Active
          </button>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("inactive")}>
            Inactive
          </button>
          <button className="btn btn-sm btn-outline-danger" onClick={() => runBulkAction("delete")}>
            Delete
          </button>
        </div>

        {/* Table */}
        <div>
          <table className="table table-hover gap-2 align-middle" style={{ tableLayout: "fixed", width: "100%" }}>
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all service locations on this page"
                  />
                </th>
                <th>Sr.</th>
                <th>Location Name</th>
                <th>Status</th>
                <th className="d-flex flex-row justify-content-start">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                paginatedCities.length > 0 &&
                paginatedCities.map((c, idx) => (
                  <tr key={c._id || idx}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(c._id)}
                        onChange={() => toggleRowSelection(c._id)}
                        aria-label={`Select ${c.name || "service location"}`}
                      />
                    </td>
                    <td>{startIndex + idx + 1}</td>
                    <td>{c.name || "-"}</td>
                    <td>
                      {c.isActive ? (
                        <span className="badge bg-success" role="button" onClick={() => toggleCityStatus(c)}>Active</span>
                      ) : (
                        <span className="badge bg-secondary" role="button" onClick={() => toggleCityStatus(c)}>Inactive</span>
                      )}
                    </td>
                    <td className="d-flex flex-row justify-content-start">
                      <button
                        className="btn btn-sm btn-outline-custom me-2"
                        onClick={() => editCity(c._id)}
                      >
                        <CgPen size={16} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => deleteCity(c._id)}
                      >
                        <CgTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}

              {!loading && paginatedCities.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-4">
                    No service locations found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex flex-wrap justify-content-between align-items-center m-3 gap-2">
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() => setPageNo((prev) => Math.max(prev - 1, 1))}
            disabled={pageNo === 1}
          >
            Prev
          </button>
          <span className="mx-2">
            Page {pageNo} of {Math.ceil(filteredCities.length / limit) || 1}
          </span>
          <button
            className="btn btn-outline-custom btn-sm"
            onClick={() =>
              setPageNo((prev) =>
                prev < Math.ceil(filteredCities.length / limit)
                  ? prev + 1
                  : prev
              )
            }
            disabled={pageNo >= Math.ceil(filteredCities.length / limit)}
          >
            Next
          </button>
        </div>
      </div>

      {/* Add City Modal */}
      <Modal show={addModel} onHide={() => setAddModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Service Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleAddCity}>
            <div className="mb-3">
              <label className="form-label">Location Name</label>
              <input
                type="text"
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <Spinnerr show={loading} /> : "Add Service Location"}
            </button>
          </form>
        </Modal.Body>
      </Modal>
      {/* Edit City Modal */}
      <Modal show={editModel} onHide={() => setEditModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Service Location</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleEditCity}>
            <div className="mb-3">
              <div className="mb-3">Current Name: {name}</div>
              <label className="form-label">Location Name</label>
              <input
                type="text"
                className="form-control"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                disabled={loading}
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? <Spinnerr show={loading} /> : "Update Service Location"}
            </button>
          </form>
        </Modal.Body>
      </Modal>

      {/* Delete City Modal */}
      <CustomModal
        show={deleteModel}
        title="Confirm Delete"
        message="Are you sure you want to delete this service location?"
        onConfirm={() => {
          handleDeleteCity(deleteId);
          setDeleteModel(false);
        }}
        onCancel={() => setDeleteModel(false)}
      />

      <Modal show={viewModel} onHide={() => setViewModel(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Service Location Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <div className="text-muted small">Location Name</div>
            <div className="fw-semibold">{viewCity?.name || "-"}</div>
          </div>
          <div>
            <div className="text-muted small">Status</div>
            <span className={`badge ${viewCity?.isActive ? "bg-success" : "bg-secondary"}`}>
              {viewCity?.isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default CitiesList;
